import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import didUpdate from "@ember/render-modifiers/modifiers/did-update";
import willDestroy from "@ember/render-modifiers/modifiers/will-destroy";
import { cancel } from "@ember/runloop";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import discourseLater from "discourse/lib/later";
import Composer from "discourse/models/composer";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";
import FrlDebugModal from "./frl-debug-modal";

export default class FrlComposerInfo extends Component {
  @service modal;
  @service site;
  @service siteSettings;

  @tracked rawData = null;
  @tracked wait = 0;

  counter = null;
  fetchId = 0;

  get limitType() {
    const composerAction = this.args.model?.action;

    if (composerAction === Composer.CREATE_TOPIC) {
      return "topic";
    } else if (composerAction === Composer.REPLY) {
      return "post";
    }

    return null;
  }

  get data() {
    return this.limitType ? this.rawData?.[this.limitType] : null;
  }

  get isUnlimited() {
    return this.data?.limit === "∞";
  }

  get debugEnabled() {
    return this.siteSettings.flexible_rate_limits_debug;
  }

  get showCounter() {
    const remaining = this.data?.remaining;
    return typeof remaining === "number" && remaining < 1 && this.wait > 0;
  }

  get formattedCounter() {
    const wait = this.wait;

    if (wait >= 3600) {
      return `${Math.floor(wait / 3600)}h`;
    }

    if (wait >= 60) {
      return `${Math.floor(wait / 60)}m`;
    }

    return `${wait}s`;
  }

  @action
  async fetchData() {
    const fetchId = ++this.fetchId;
    const limitType = this.limitType;

    this.cancelCounter();
    this.setRawData(null);

    if (!limitType || this.args.model.privateMessage) {
      return;
    }

    const categoryId =
      this.args.model.categoryId || this.site.uncategorized_category_id;

    try {
      const result = await ajax(
        `/flexible_rate_limits/${categoryId}/${limitType}.json`,
        { type: "POST" }
      );

      if (fetchId !== this.fetchId || this.isDestroying || this.isDestroyed) {
        return;
      }

      this.wait = result?.[limitType]?.wait || 0;
      this.setRawData(result);
      this.countDown();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  setRawData(rawData) {
    this.rawData = rawData;
    document
      .querySelector(".wmd-controls")
      ?.classList.toggle("with-frl-composer-info", !!rawData);
  }

  countDown() {
    if (!this.showCounter) {
      return;
    }

    this.counter = discourseLater(() => {
      this.wait -= 1;
      this.countDown();
    }, 1000);
  }

  cancelCounter() {
    if (this.counter) {
      cancel(this.counter);
      this.counter = null;
    }
  }

  @action
  teardown() {
    this.fetchId++;
    this.cancelCounter();
    document
      .querySelector(".wmd-controls")
      ?.classList.remove("with-frl-composer-info");
  }

  @action
  showDebugModal() {
    if (!this.debugEnabled) {
      return;
    }

    const info = this;

    this.modal.show(FrlDebugModal, {
      model: {
        get limitType() {
          return info.limitType;
        },
        get data() {
          return info.data;
        },
        get newUser() {
          return info.rawData?.new_user;
        },
        get showCounter() {
          return info.showCounter;
        },
        get wait() {
          return info.wait;
        },
      },
    });
  }

  <template>
    <div
      class="frl-composer-info"
      {{didInsert this.fetchData}}
      {{didUpdate
        this.fetchData
        @model.categoryId
        @model.action
        @model.privateMessage
      }}
      {{willDestroy this.teardown}}
    >
      {{#if this.data}}
        {{#unless this.isUnlimited}}
          {{! eslint-disable-next-line ember/template-no-invalid-interactive }}
          <div
            class={{dConcatClass
              "frl-composer-info-data"
              (if this.debugEnabled "click")
            }}
            title={{if this.debugEnabled "click for more info"}}
            role={{if this.debugEnabled "button"}}
            {{on "click" this.showDebugModal}}
          >
            {{i18n
              "flexible_rate_limits.composer_info"
              remaining=this.data.remaining
              limit=this.data.limit
              limitType=this.limitType
            }}

            {{#if this.showCounter}}
              <span class="frl-counter">({{this.formattedCounter}})</span>
            {{/if}}

            {{#if this.debugEnabled}}
              {{dIcon "circle-question"}}
            {{/if}}
          </div>
        {{/unless}}
      {{/if}}
    </div>
  </template>
}
